import React, { useState } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, RefreshCw, ExternalLink, Upload } from 'lucide-react';
import { Spinner, Alert } from 'react-bootstrap';
import { useInstanceSetup, ConfigItemStatus } from '../../hooks/setup/useInstanceSetup';
import { buildingChecksApi } from '../../api/buildingChecksApi';
import { componentFieldsApi } from '../../api/componentFieldsApi';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import ConfigImportModal from './ConfigImportModal';
import InstanceNameModal from './InstanceNameModal';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import './SetupPage.css';

const SetupPage: React.FC = () => {
    // Set document title
    useDocumentTitle('Konfigurationsstatus');

    const {
        loading,
        requiredComplete,
        requiredItems,
        recommendedItems,
        optionalItems,
        refresh,
    } = useInstanceSetup();
    const { refresh: refreshComponentTypes } = useBuildComponentTypes();
    const [showImportModal, setShowImportModal] = useState(false);
    const [showInstanceNameModal, setShowInstanceNameModal] = useState(false);
    const [creatingTemplates, setCreatingTemplates] = useState(false);
    const [creatingDefaultTypes, setCreatingDefaultTypes] = useState(false);
    const [templatesAlert, setTemplatesAlert] = useState<{ variant: 'success' | 'danger'; message: string } | null>(null);

    // Skickbedömning system templates are created from here (or the admin banner), never on page open (D5, D28)
    const handleCreateAssessmentTemplates = async () => {
        setCreatingTemplates(true);
        setTemplatesAlert(null);
        try {
            const created = await buildingChecksApi.ensureAssessmentTemplates();
            setTemplatesAlert({
                variant: 'success',
                message: created.length > 0
                    ? `${created.length} ${created.length === 1 ? 'mall' : 'mallar'} för skickbedömning skapade`
                    : 'Alla mallar för skickbedömning finns redan'
            });
            refresh();
        } catch (error) {
            console.error('Error creating assessment templates:', error);
            setTemplatesAlert({ variant: 'danger', message: error instanceof Error ? error.message : 'Kunde inte skapa mallarna' });
        } finally {
            setCreatingTemplates(false);
        }
    };

    // iOS-relied-on default types, created by an admin from here or the banner, never on page open
    const handleCreateDefaultTypes = async () => {
        setCreatingDefaultTypes(true);
        setTemplatesAlert(null);
        try {
            const created = await componentFieldsApi.ensureDefaultTypes();
            setTemplatesAlert({
                variant: 'success',
                message: created.length > 0
                    ? `${created.length} ${created.length === 1 ? 'standardtyp' : 'standardtyper'} skapade`
                    : 'Alla standardtyper finns redan'
            });
            refresh();
            refreshComponentTypes();
        } catch (error) {
            console.error('Error creating default types:', error);
            setTemplatesAlert({ variant: 'danger', message: error instanceof Error ? error.message : 'Kunde inte skapa standardtyperna' });
        } finally {
            setCreatingDefaultTypes(false);
        }
    };

    const handleImportClose = (didImport: boolean) => {
        setShowImportModal(false);
        if (didImport) {
            refresh();
            refreshComponentTypes();
        }
    };

    const handleInstanceNameClose = (didSave: boolean) => {
        setShowInstanceNameModal(false);
        if (didSave) {
            refresh();
        }
    };

    const configuredRequired = requiredItems.filter(i => i.configured).length;

    return (
        <div className="setup-page">
            <BreadcrumbComponent items={[{ text: 'Konfigurationsstatus' }]} />
            <nav className="navbar navbar-expand-lg navbar-light bg-light setup-navbar">
                <div className="container-fluid">
                    <div className="navbar-brand setup-brand">
                        <h1 className="setup-title page-title">Konfigurationsstatus</h1>
                        <p className="setup-description">
                            Kontrollera att alla nödvändiga konfigurationer är på plats för instansen
                        </p>
                    </div>
                    <div className="navbar-nav ms-auto d-flex flex-row gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setShowImportModal(true)}
                        >
                            <Upload size={14} />
                            <span className="ms-1">Importera konfiguration</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={refresh}
                            disabled={loading}
                        >
                            <RefreshCw size={14} className={loading ? 'spin' : ''} />
                            <span className="ms-1">Uppdatera</span>
                        </button>
                    </div>
                </div>
            </nav>

            <div className="container-fluid setup-content">
                <div className="setup-summary">
                    {loading ? (
                        <span className="text-muted">Laddar...</span>
                    ) : (
                        <span className={requiredComplete ? 'text-success' : 'text-danger'}>
                            {configuredRequired} av {requiredItems.length} obligatoriska konfigurerade
                        </span>
                    )}
                </div>

                <ConfigSection
                    title="Obligatoriskt"
                    borderColor="#dc3545"
                    items={requiredItems}
                    itemActions={{ instanceName: () => setShowInstanceNameModal(true) }}
                />

                {templatesAlert && (
                    <Alert variant={templatesAlert.variant} dismissible onClose={() => setTemplatesAlert(null)} className="mt-3">
                        {templatesAlert.message}
                    </Alert>
                )}

                <ConfigSection
                    title="Rekommenderat"
                    borderColor="#fd7e14"
                    items={recommendedItems}
                    itemActions={{
                        conditionAssessmentTemplates: () => { if (!creatingTemplates) handleCreateAssessmentTemplates(); },
                        requiredDefaultTypes: () => { if (!creatingDefaultTypes) handleCreateDefaultTypes(); }
                    }}
                    itemActionLabels={{
                        conditionAssessmentTemplates: creatingTemplates ? 'Skapar...' : 'Skapa mallar',
                        requiredDefaultTypes: creatingDefaultTypes ? 'Skapar...' : 'Skapa'
                    }}
                />

                <ConfigSection
                    title="Valfritt"
                    borderColor="#0d6efd"
                    items={optionalItems}
                />
            </div>

            <ConfigImportModal show={showImportModal} onClose={handleImportClose} />
            <InstanceNameModal show={showInstanceNameModal} onClose={handleInstanceNameClose} />
        </div>
    );
};

interface ConfigSectionProps {
    title: string;
    borderColor: string;
    items: ConfigItemStatus[];
    /** In-page configure actions (e.g. open a modal), keyed by item key; takes precedence over settingsLink */
    itemActions?: Record<string, () => void>;
    /** Button label for an in-page action (default "Konfigurera") */
    itemActionLabels?: Record<string, string>;
}

const ConfigSection: React.FC<ConfigSectionProps> = ({ title, borderColor, items, itemActions, itemActionLabels }) => (
    <div className="setup-section" style={{ borderLeftColor: borderColor }}>
        <h2 className="setup-section-title">{title}</h2>
        <div className="setup-items">
            {items.map(item => (
                <div key={item.key} className="setup-item">
                    <div className="setup-item-status">
                        {item.loading ? (
                            <Spinner animation="border" size="sm" />
                        ) : item.configured ? (
                            <CheckCircle size={20} color="#198754" />
                        ) : (
                            <XCircle size={20} color="#dc3545" />
                        )}
                    </div>
                    <div className="setup-item-info">
                        <div className="setup-item-label">{item.label}</div>
                        <div className="setup-item-description">{item.description}</div>
                        {item.subFields && (
                            <div className="setup-sub-fields">
                                {item.subFields.map(sf => (
                                    <div key={sf.key} className="setup-sub-field">
                                        {sf.configured ? (
                                            <CheckCircle size={14} color="#198754" />
                                        ) : (
                                            <XCircle size={14} color="#dc3545" />
                                        )}
                                        <span className="setup-sub-field-label">{sf.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="setup-item-action">
                        {itemActions?.[item.key] && !(item.configured && item.settingsLink) ? (
                            // In-page action; once configured, an item with a settings link goes there instead
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={itemActions[item.key]}
                            >
                                {itemActionLabels?.[item.key] ?? 'Konfigurera'}
                            </button>
                        ) : item.settingsLink ? (
                            <Link to={item.settingsLink} className="btn btn-outline-primary btn-sm">
                                Konfigurera <ExternalLink size={12} />
                            </Link>
                        ) : (
                            <code className="setup-item-path">{item.path}</code>
                        )}
                    </div>
                </div>
            ))}
        </div>
    </div>
);

export default SetupPage;
